import React, { useState, useEffect } from 'react';
import { getAIUsageStats } from '../services/api';
import { useToast } from '../App';
import { FiActivity, FiCpu, FiDollarSign, FiZap, FiClock, FiRefreshCw } from 'react-icons/fi';

const PROVIDERS = [
    { value: 'openai', label: 'OpenAI', logo: '/gpt.png', color: '#10a37f' },
    { value: 'claude', label: 'Claude (Anthropic)', logo: '/claude.png', color: '#d97706' },
    { value: 'gemini', label: 'Gemini (Google)', logo: '/gemini.png', color: '#4285f4' },
];

export default function Monitoring() {
    const addToast = useToast();
    const [usageStats, setUsageStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadStats = async () => {
        setLoading(true);
        try {
            const { data } = await getAIUsageStats();
            setUsageStats(data);
        } catch {
            addToast('error', 'Impossible de charger les statistiques', 'Erreur');
        }
        setLoading(false);
    };

    useEffect(() => { loadStats(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            <div className="page-header">
                <div>
                    <h2><FiActivity className="me-2" style={{ color: 'var(--primary)' }} />Monitoring — Consommation IA</h2>
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                        Suivez vos tokens, coûts et requêtes pour chaque provider et modèle
                    </p>
                </div>
                <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={loadStats} disabled={loading}>
                    <FiRefreshCw className={loading ? 'spin' : ''} /> Actualiser
                </button>
            </div>
            {loading && !usageStats ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                    <p className="text-muted mt-2" style={{ fontSize: '0.82rem' }}>Chargement des statistiques...</p>
                </div>
            ) : !usageStats || usageStats.totals.request_count === 0 ? (
                <div className="card">
                    <div className="card-body text-center py-5">
                        <FiActivity style={{ fontSize: '3rem', color: 'var(--gray-300)', marginBottom: '1rem' }} />
                        <h5 className="text-muted">Aucune consommation enregistrée</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                            Les statistiques apparaîtront ici après votre première génération IA.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Global Stats Cards */}
                    <div className="row g-3 mb-4">
                        {[
                            { icon: <FiZap />, label: 'Requêtes totales', value: usageStats.totals.request_count, color: '#6366f1' },
                            { icon: <FiCpu />, label: 'Tokens Entrée', value: usageStats.totals.tokens_in.toLocaleString(), color: '#10a37f' },
                            { icon: <FiCpu />, label: 'Tokens Sortie', value: usageStats.totals.tokens_out.toLocaleString(), color: '#d97706' },
                            { icon: <FiDollarSign />, label: 'Coût total estimé', value: `$${usageStats.totals.total_cost.toFixed(4)}`, color: '#ef4444' },
                        ].map((s, i) => (
                            <div className="col-md-3 col-6" key={i}>
                                <div className="card h-100" style={{ border: `1px solid ${s.color}20` }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center gap-2 mb-2" style={{ color: s.color, fontSize: '0.78rem', fontWeight: 500 }}>
                                            {s.icon} {s.label}
                                        </div>
                                        <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--gray-800)', lineHeight: 1 }}>{s.value}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row g-4 mb-4">
                        {/* Per-provider */}
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-header d-flex align-items-center gap-2">
                                    <FiActivity style={{ color: 'var(--primary)' }} />
                                    <span>Consommation par Provider</span>
                                </div>
                                <div className="card-body">
                                    {usageStats.by_provider.map((p) => {
                                        const info = PROVIDERS.find((pr) => pr.value === p.provider);
                                        const pct = usageStats.totals.total_cost > 0 ? (p.total_cost / usageStats.totals.total_cost) * 100 : 0;
                                        return (
                                            <div key={p.provider} className="mb-4">
                                                <div className="d-flex align-items-center justify-content-between mb-1">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <img src={info?.logo} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                                                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{info?.label || p.provider}</span>
                                                    </div>
                                                    <span style={{ fontSize: '0.8rem', fontFamily: 'Consolas, monospace', fontWeight: 600, color: info?.color || 'var(--gray-600)' }}>
                                                        ${p.total_cost.toFixed(4)}
                                                    </span>
                                                </div>
                                                <div style={{ height: '8px', background: 'var(--gray-100)', borderRadius: '4px', overflow: 'hidden' }}>
                                                    <div style={{
                                                        width: `${Math.max(pct, 3)}%`, height: '100%',
                                                        background: `linear-gradient(90deg, ${info?.color || 'var(--primary)'}90, ${info?.color || 'var(--primary)'})`,
                                                        borderRadius: '4px', transition: 'width 0.4s ease',
                                                    }} />
                                                </div>
                                                <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                                                    <span>{p.request_count} requête{p.request_count > 1 ? 's' : ''}</span>
                                                    <span>↓ {p.tokens_in.toLocaleString()} in · ↑ {p.tokens_out.toLocaleString()} out</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Per-model */}
                        <div className="col-md-6">
                            <div className="card h-100">
                                <div className="card-header d-flex align-items-center gap-2">
                                    <FiCpu style={{ color: 'var(--primary)' }} />
                                    <span>Consommation par Modèle</span>
                                </div>
                                <div className="card-body">
                                    {usageStats.by_model.map((m, i) => {
                                        const info = PROVIDERS.find((pr) => pr.value === m.provider);
                                        return (
                                            <div key={i} className="d-flex align-items-center justify-content-between p-2 mb-2"
                                                style={{ background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)', borderLeft: `3px solid ${info?.color || 'var(--primary)'}` }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    <img src={info?.logo} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                                                    <div>
                                                        <code style={{ fontSize: '0.76rem', fontWeight: 600 }}>{m.model}</code>
                                                        <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>{m.request_count} req · {(m.tokens_in + m.tokens_out).toLocaleString()} tokens</div>
                                                    </div>
                                                </div>
                                                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)', fontFamily: 'Consolas, monospace' }}>
                                                    ${m.total_cost.toFixed(4)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    {usageStats.recent.length > 0 && (
                        <div className="card">
                            <div className="card-header d-flex align-items-center gap-2">
                                <FiClock style={{ color: 'var(--primary)' }} />
                                <span>Activité récente</span>
                                <span className="ms-auto" style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                                    20 dernières requêtes
                                </span>
                            </div>
                            <div className="card-body p-0">
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.78rem' }}>
                                        <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card, white)', zIndex: 1 }}>
                                            <tr>
                                                <th style={{ paddingLeft: '1rem' }}>Date</th>
                                                <th>Provider</th>
                                                <th>Modèle</th>
                                                <th className="text-end">Tokens ↓</th>
                                                <th className="text-end">Tokens ↑</th>
                                                <th className="text-end" style={{ paddingRight: '1rem' }}>Coût</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {usageStats.recent.map((log) => {
                                                const info = PROVIDERS.find((pr) => pr.value === log.provider);
                                                const date = log.created_at ? new Date(log.created_at) : null;
                                                return (
                                                    <tr key={log.id}>
                                                        <td style={{ whiteSpace: 'nowrap', paddingLeft: '1rem' }}>
                                                            {date ? date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1">
                                                                <img src={info?.logo} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                                                                {info?.label || log.provider}
                                                            </div>
                                                        </td>
                                                        <td><code style={{ fontSize: '0.72rem' }}>{log.model}</code></td>
                                                        <td className="text-end">{log.tokens_in.toLocaleString()}</td>
                                                        <td className="text-end">{log.tokens_out.toLocaleString()}</td>
                                                        <td className="text-end" style={{ fontWeight: 600, paddingRight: '1rem' }}>${log.estimated_cost.toFixed(4)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
